import { Link } from "react-router-dom";
import { ArrowRight, Gem, Hand, Package } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { useProducts } from "@/hooks/useProducts";

const HERO_IMG =
  "https://images.unsplash.com/photo-1623421536280-fa8ae906ed1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxlZGl0b3JpYWwlMjB3b21hbiUyMHdlYXJpbmclMjBlbGVnYW50JTIwamV3ZWxyeSUyMG5ldXRyYWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NjU1MTkxNHww&ixlib=rb-4.1.0&q=85";
const STORY_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_beads-premium-store/artifacts/io79bnpc_Bracelet%20WA0009%20product%20display.png";

const VALUES = [
  { icon: Hand, title: "Dirangkai Tangan", text: "Setiap butir dipilih dan dirangkai manual, bukan produksi mesin." },
  { icon: Gem, title: "Material Pilihan", text: "Kristal, batu kaca, dan charm berlapis emas atau perak." },
  { icon: Package, title: "Siap Diberikan", text: "Setiap pesanan dikemas dalam pouch dan kotak kaku." },
];

export default function Home() {
  const { data: products = [] } = useProducts();
  const featured = products.slice(0, 4);
  const spotlight = products[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grid items-stretch border-b border-line lg:grid-cols-12">
        <div className="grain relative order-2 flex flex-col justify-center bg-paper px-5 py-20 md:px-10 lg:order-1 lg:col-span-6 lg:px-16 lg:py-32">
          <p className="label-eyebrow fade-up text-sand">Gelang Beads Buatan Tangan</p>
          <h1
            className="fade-up mt-7 font-display text-[44px] font-light leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "120ms" }}
          >
            Gelang beads<br />
            yang dirangkai<br />
            <em className="italic text-sand">satu per satu.</em>
          </h1>
          <p
            className="fade-up mt-8 max-w-md text-base leading-relaxed text-stone"
            style={{ animationDelay: "240ms" }}
          >
            Koleksi kecil gelang beads buatan tangan: kristal, batu kaca, dan charm logam yang dipilih
            satu per satu. Setiap desain dibuat dalam jumlah terbatas.
          </p>
          <div className="fade-up mt-11 flex flex-col gap-4 sm:flex-row" style={{ animationDelay: "360ms" }}>
            <Link
              to="/koleksi"
              className="group inline-flex items-center justify-center gap-3 bg-ink px-9 py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
              data-testid="hero-shop-cta"
            >
              Lihat Koleksi
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/tentang-kami"
              className="inline-flex items-center justify-center border border-ink px-9 py-4 text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
              data-testid="hero-story-cta"
            >
              Tentang Kami
            </Link>
          </div>

          <dl className="mt-16 grid max-w-md grid-cols-3 gap-6 border-t border-line pt-8">
            {[
              [`${products.length}`, "Desain tersedia"],
              ["1–2", "Hari proses"],
              ["100%", "Rangkaian tangan"],
            ].map(([n, l]) => (
              <div key={l}>
                <dt className="font-display text-3xl text-ink">{n}</dt>
                <dd className="mt-1 text-[11px] uppercase tracking-[0.14em] text-stone">{l}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative order-1 min-h-[380px] lg:order-2 lg:col-span-6 lg:min-h-[720px]">
          <img src={HERO_IMG} alt="Perempuan mengenakan perhiasan Zoya" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/10" />
          {spotlight && (
            <Link
              to={`/produk/${spotlight.slug}`}
              className="absolute bottom-6 left-6 flex items-center gap-4 bg-paper/95 p-4 backdrop-blur-sm transition-transform duration-500 hover:-translate-y-1 md:bottom-10 md:left-10"
              data-testid="hero-spotlight"
            >
              <img src={spotlight.images?.[0]} alt={spotlight.name} className="h-16 w-16 object-cover" />
              <div>
                <p className="label-eyebrow text-[9px] text-sand">Pilihan kami</p>
                <p className="mt-1 font-display text-lg text-ink">{spotlight.name}</p>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 lg:px-16">
        <div className="grid gap-12 md:grid-cols-3">
          {VALUES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-5">
              <Icon size={22} strokeWidth={1.3} className="mt-1 shrink-0 text-sand" />
              <div>
                <h3 className="font-display text-xl text-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-16" data-testid="featured-section">
        <div className="flex flex-col gap-5 border-t border-line pt-14 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="label-eyebrow text-sand">Katalog</p>
            <h2 className="mt-4 font-display text-3xl leading-tight text-ink md:text-4xl">
              Pilihan koleksi
            </h2>
          </div>
          <Link
            to="/koleksi"
            className="link-underline inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-ink"
            data-testid="featured-view-all"
          >
            Semua Produk <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-16 lg:grid-cols-4 lg:gap-x-8">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="mt-28 border-y border-line bg-linen">
        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 py-20 md:px-10 lg:grid-cols-12 lg:px-16 lg:py-28">
          <div className="relative lg:col-span-5">
            <img src={STORY_IMG} alt="Gelang beads buatan tangan" className="aspect-[4/5] w-full object-cover" />
            <div className="absolute -bottom-6 -right-4 hidden bg-ink px-7 py-6 text-paper lg:block">
              <p className="font-display text-2xl">Buatan tangan</p>
              <p className="label-eyebrow mt-1 text-[9px] text-sand">Jumlah terbatas</p>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="label-eyebrow text-sand">Tentang Kami</p>
            <h2 className="mt-5 font-display text-3xl leading-[1.15] text-ink md:text-4xl">
              Toko kecil, dikerjakan sendiri, satu gelang setiap kali.
            </h2>
            <p className="mt-7 text-base leading-relaxed text-stone">
              Zoya Beads adalah toko kecil yang baru mulai. Semua gelang di sini kami rangkai sendiri:
              memilih beads, menyusun urutan warna, mengukur elastis, lalu memastikan simpulnya rapi.
            </p>
            <p className="mt-5 text-base leading-relaxed text-stone">
              Karena stok beads yang kami punya terbatas, tiap desain dibuat dalam jumlah kecil.
              Kalau ada yang kamu suka, sebaiknya jangan menunggu terlalu lama.
            </p>
            <Link
              to="/tentang-kami"
              className="link-underline mt-9 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-ink"
              data-testid="story-read-more"
            >
              Baca Selengkapnya <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CUSTOM ORDER */}
      <section className="mx-auto max-w-[1440px] px-5 pt-24 md:px-10 lg:px-16" data-testid="home-custom-section">
        <div className="grid items-center gap-10 border border-line p-8 md:grid-cols-12 md:p-14">
          <div className="md:col-span-8">
            <p className="label-eyebrow text-sand">Custom Order</p>
            <h2 className="mt-4 font-display text-3xl leading-tight text-ink md:text-4xl">
              Mau warna dan charm pilihanmu sendiri?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-stone">
              Kirim warna, charm, dan ukuran lewat WhatsApp — kami rangkai khusus untuk kamu. Mulai
              dari Rp 30.000.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/custom"
              className="inline-flex items-center gap-3 bg-ink px-9 py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
              data-testid="home-custom-cta"
            >
              Lihat Cara Custom <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-24 text-center md:px-10 lg:px-16">
        <p className="label-eyebrow text-sand">Koleksi</p>
        <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-tight text-ink md:text-4xl">
          Lihat semua gelang yang tersedia
        </h2>
        <Link
          to="/koleksi"
          className="mt-10 inline-flex items-center gap-3 bg-ink px-10 py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
          data-testid="bottom-shop-cta"
        >
          Mulai Belanja <ArrowRight size={15} />
        </Link>
      </section>
    </div>
  );
}
